import {getShopCurrency, getShopName, JURISDICTIONS} from "../../../config";
import {TYPES} from "./data/types";


function asPercentage(num) {
  if (isNaN(num)) return null;

  return Math.ceil(100 * num) + "%";
}

function percentage(newNumber, oldNumber) {
  return (newNumber - oldNumber) / oldNumber;
}

// Aggregate data depending on dataFetcher and computes increase from previous data
// NB: some data should be summed for every datum, like revenues and orders,
// but others should use only the most recent information, like active
// subscriptions. The problem is, even in that case, we need to keep track of
// these last values for each jurisdiction, and then only sum them together.
//
// E.g. for yearly data we want the active subs number from the last month,
// summed over the different jurisdictions.
function aggregateDataPerJurisdictionFor(periodicData, dataFetcher, aggregator) {
  if (periodicData.length === 0) return {};

  const valueOrZero = (datum) => {
    if (!datum) return 0;

    const value = dataFetcher(datum);
    return isNaN(value) ? 0 : value;
  }
  
  return periodicData.reduce((acc, [date, data]) => {
    // Aggregate from any jurisdiction
    Object.entries(data).forEach(([id, jurisData]) => {
      acc[id] = aggregator(acc[id], valueOrZero(jurisData));
    })
    
    return acc;
  }, {});
}

const triptique = (
    periodicData, previousExerciseData, dataFetcher,
    maybeJurisdictionId = null,
    aggregator = (acc = 0, value = 0) => acc + value,
) => {
  const currentPerJurisdiction = aggregateDataPerJurisdictionFor(periodicData, dataFetcher, aggregator);
  const previousPerJurisdiction = aggregateDataPerJurisdictionFor(previousExerciseData, dataFetcher, aggregator);

  const maybeSum = (aggregate, maybeJurisdictionId) => {
    if (maybeJurisdictionId) {
      return aggregate[maybeJurisdictionId] || NaN;
    } else {
      return Object.values(aggregate).reduce((acc, val) => acc + val, 0);
    }
  }

  const current = maybeSum(currentPerJurisdiction, maybeJurisdictionId)
  const previous = maybeSum(previousPerJurisdiction, maybeJurisdictionId)

  const increase = asPercentage(percentage(current, previous));

  return { current, previous, increase }
}

export function jurisdictionsDataFor(filteredData, previousExerciseData) {

  return Object.values(JURISDICTIONS).map((id) => {
    const orders = triptique(filteredData, previousExerciseData, TYPES.ORDERS.dataFetcher, id);
    const revenues = triptique(filteredData, previousExerciseData, TYPES.REVENUES.dataFetcher, id);

    return {
      name: getShopName(id),
      shopId: id,
      orders: {
        value: orders.current,
        increase: orders.increase,
      },
      revenue: {
        value: {
          amount: revenues.current,
          currency: getShopCurrency(id),
        },
        increase: revenues.increase,
      },
    };
  })
}

export function revenuesDataFor(filteredData, previousExerciseData, jurisdictionId) {
  if (jurisdictionId === null) return []; // Cannot add prices in different currencies

  const totalRevenues = triptique(filteredData, previousExerciseData, TYPES.REVENUES.dataFetcher, jurisdictionId);
  const subRevenues = triptique(filteredData, previousExerciseData, TYPES.REVENUES_SUBS.dataFetcher, jurisdictionId);
  const otRevenues = triptique(filteredData, previousExerciseData, TYPES.REVENUES_OT.dataFetcher, jurisdictionId);
  // const avgOrder = triptique(filteredData, previousExerciseData, TYPES.REVENUES.dataFetcher, jurisdictionId);
  // const lostRevenue = triptique(filteredData, previousExerciseData, TYPES.REVENUES.dataFetcher, jurisdictionId);
  
  return [
    {
      name: "Total Revenue",
      value: {
        amount: totalRevenues.current,
        currency: getShopCurrency(jurisdictionId),
      },
      increase: totalRevenues.increase,
    },
    {
      name: "Subscription Revenue",
      value: {
        amount: subRevenues.current,
        currency: getShopCurrency(jurisdictionId),
      },
      increase: subRevenues.increase,
    },
    {
      name: "One-Time Order Revenue",
      value: {
        amount: otRevenues.current,
        currency: getShopCurrency(jurisdictionId),
      },
      increase: otRevenues.increase,
    },
    {
      name: "Average Order",
      value: {
        amount: NaN,
        currency: getShopCurrency(jurisdictionId),
      },
      increase: NaN,
    },
    // {
    //   name: "Cancelled Subscription Lost Revenue",
    //   value: {
    //     amount: NaN,
    //     currency: getShopCurrency(jurisdictionId),
    //   },
    //   increase: NaN,
    // },
  ];
}

function aggregate_only_last_value(acc, val) {
  return val === 0 ? acc : val
}

export function ordersDataFor(filteredData, previousExerciseData, jurisdictionId) {

  const subs = triptique(filteredData, previousExerciseData, TYPES.SUBS.dataFetcher, jurisdictionId, aggregate_only_last_value);
  const newSubs = triptique(filteredData, previousExerciseData, TYPES.SUBS_NEW.dataFetcher, jurisdictionId);
  // const pausedSubs = triptique(filteredData, previousExerciseData, TYPES.SUBS_PAUSED.dataFetcher, jurisdictionId);
  const cancelledSubs = triptique(filteredData, previousExerciseData, TYPES.SUBS_CANCELLED.dataFetcher, jurisdictionId);
  const otOrders = triptique(filteredData, previousExerciseData, TYPES.ORDERS_ONETIME.dataFetcher, jurisdictionId);
  
  return [
    // {
    //   name: "Total Orders",
    //   value: totalOrders.current,
    //   increase: totalOrders.increase,
    // },
    {
      name: "Active Subscriptions Orders",
      tooltip: "Active subscriptions Orders during the last period of time",
      value: subs.current,
      increase: subs.increase,
    },
    {
      name: "New Subscriptions",
      value: newSubs.current,
      increase: newSubs.increase,
    },
    // This is not a good metric, as it doesn't take into account resumed subscriptions.
    // {
    //   name: "Paused Subscriptions",
    //   value: pausedSubs.current,
    //   increase: pausedSubs.increase,
    // },
    {
      name: "Cancelled Subscription",
      value: cancelledSubs.current,
      increase: cancelledSubs.increase,
      flipPositive: true, // Cancellations going down => positive thing
    },
    {
      name: "One-Time Orders",
      value: otOrders.current,
      increase: otOrders.increase,
    },
  ];
}

export function clientsDataFor(filteredData, previousExerciseData, jurisdictionId) {

  // const customers_total = triptique(filteredData, previousExerciseData, TYPES.CUSTOMERS_ACTIVE.dataFetcher, jurisdictionId, aggregate_only_last_value);
  const customers_active = triptique(filteredData, previousExerciseData, TYPES.CUSTOMERS_ACTIVE.dataFetcher, jurisdictionId, aggregate_only_last_value);
  
  return [
    // {
    //   name: "Total Clients",
    //   value: NaN,
    //   increase: NaN,
    // },
    {
      name: "Active Clients",
      value: customers_active.current,
      increase: customers_active.increase,
    },
    // {
    //   name: "New Clients",
    //   value: NaN,
    //   increase: NaN,
    // },
    // {
    //   name: "New Registrations",
    //   value: NaN,
    //   increase: NaN,
    // },
    // {
    //   name: "New Email Subscribers",
    //   value: NaN,
    //   increase: NaN,
    // },
  ];
}